import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Coins, TrendingUp, Database, DollarSign, ChevronDown, ChevronUp, Loader2, RefreshCw } from 'lucide-react';
import { cn } from '../lib/utils';
import { useSafeMode } from '../contexts/SafeModeContext';
import api from '../services/api';

/**
 * Token Utilization Dashboard for Admin Page
 * Shows model usage, costs, and margins
 */
const TokenUtilizationDashboard = ({ period = 30 }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [utilizationRes, costRes] = await Promise.all([
        api.get(`/api/admin/metrics/token-utilization?days=${period}`),
        api.get(`/api/admin/metrics/token-utilization/cost-analysis?days=${period}`)
      ]);
      setData({
        utilization: utilizationRes.data,
        costs: costRes.data
      });
    } catch (err) {
      console.error('Failed to fetch token utilization:', err);
      setError(err.response?.data?.detail || 'Failed to load token data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period]);

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toLocaleString() || '0';
  };

  const formatCurrency = (num) => {
    return `$${num?.toFixed(2) || '0.00'}`;
  };

  if (loading) {
    return (
      <Card className={cn("p-6 border", colors.bgCard, colors.borderPrimary)}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          <span className={cn("ml-2 text-sm", colors.textMuted)}>Loading token utilization...</span>
        </div>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className={cn("p-6 border", colors.bgCard, colors.borderPrimary)}>
        <div className="text-center py-4">
          <p className={cn("text-sm", colors.textMuted)}>{error || 'No data available'}</p>
          <Button variant="outline" size="sm" onClick={fetchData} className="mt-2">
            <RefreshCw className="w-4 h-4 mr-1" /> Retry
          </Button>
        </div>
      </Card>
    );
  }

  const { utilization, costs } = data;
  const summary = utilization?.summary || {};
  const costTotals = costs?.totals || {};
  const byModel = utilization?.by_model || [];
  const byFeature = utilization?.by_feature || [];

  return (
    <Card className={cn("p-6 border", colors.bgCard, colors.borderPrimary)}>
      <div className="flex items-center justify-between mb-4">
        <h3 className={cn("font-semibold flex items-center gap-2", colors.textPrimary)}>
          <Coins className="w-5 h-5 text-emerald-500" />
          Token Utilization ({period}d)
        </h3>
        <Button variant="ghost" size="sm" onClick={fetchData}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className={cn("p-3 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
          <p className={cn("text-xs font-medium", colors.textMuted)}>Total Tokens</p>
          <p className={cn("text-xl font-bold", colors.textPrimary)}>
            {formatNumber(summary.total_tokens)}
          </p>
          <p className={cn("text-xs", colors.textMuted)}>
            {summary.total_requests?.toLocaleString()} requests
          </p>
        </div>
        <div className={cn("p-3 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
          <p className={cn("text-xs font-medium", colors.textMuted)}>Est. Cost</p>
          <p className={cn("text-xl font-bold text-red-500")}>
            {formatCurrency(costTotals.estimated_cost_usd)}
          </p>
          <p className={cn("text-xs", colors.textMuted)}>to provider</p>
        </div>
        <div className={cn("p-3 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
          <p className={cn("text-xs font-medium", colors.textMuted)}>Target Revenue</p>
          <p className={cn("text-xl font-bold text-emerald-600")}>
            {formatCurrency(costTotals.estimated_revenue_usd)}
          </p>
          <p className={cn("text-xs", colors.textMuted)}>at {costTotals.target_roi_percent}% ROI</p>
        </div>
        <div className={cn("p-3 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
          <p className={cn("text-xs font-medium", colors.textMuted)}>Margin</p>
          <p className={cn("text-xl font-bold", costTotals.on_target ? "text-emerald-600" : "text-amber-500")}>
            {formatCurrency(costTotals.margin_usd)}
          </p>
          <p className={cn("text-xs", costTotals.on_target ? "text-emerald-600" : "text-amber-500")}>
            {costTotals.effective_margin_percent?.toFixed(1)}% margin
          </p>
        </div>
      </div>

      {/* Model Breakdown */}
      {byModel.length > 0 && (
        <div className="mb-4">
          <p className={cn("text-sm font-medium mb-2", colors.textSecondary)}>By Model</p>
          <div className="space-y-2">
            {byModel.slice(0, expanded ? undefined : 3).map(model => (
              <div key={model.model} className={cn("p-3 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
                <div className="flex items-center justify-between mb-1">
                  <span className={cn("text-sm font-medium", colors.textPrimary)}>{model.model || 'Unknown'}</span>
                  <span className={cn("text-sm", colors.textMuted)}>{formatNumber(model.total_tokens)} tokens</span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div>
                    <span className={colors.textMuted}>Input: </span>
                    <span className={colors.textPrimary}>{formatNumber(model.prompt_tokens)}</span>
                  </div>
                  <div>
                    <span className={colors.textMuted}>Output: </span>
                    <span className={colors.textPrimary}>{formatNumber(model.completion_tokens)}</span>
                  </div>
                  <div>
                    <span className={colors.textMuted}>Cost: </span>
                    <span className="text-red-500">{formatCurrency(model.estimated_cost_usd)}</span>
                  </div>
                  <div>
                    <span className={colors.textMuted}>Revenue: </span>
                    <span className="text-emerald-600">{formatCurrency(model.estimated_revenue_usd)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {byModel.length > 3 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className={cn("w-full mt-2", colors.textMuted)}
            >
              {expanded ? (
                <><ChevronUp className="w-4 h-4 mr-1" /> Show Less</>
              ) : (
                <><ChevronDown className="w-4 h-4 mr-1" /> Show All ({byModel.length} models)</>
              )}
            </Button>
          )}
        </div>
      )}

      {/* Feature Breakdown */}
      {byFeature.length > 0 && (
        <div>
          <p className={cn("text-sm font-medium mb-2", colors.textSecondary)}>By Feature</p>
          <div className="grid grid-cols-2 gap-2">
            {byFeature.slice(0, 6).map(feature => (
              <div 
                key={feature.feature} 
                className={cn("p-2 rounded border text-sm", colors.bgTertiary, colors.borderPrimary)}
              >
                <div className="flex justify-between">
                  <span className={cn("font-medium capitalize", colors.textPrimary)}>
                    {feature.feature || 'Unknown'}
                  </span>
                  <span className={colors.textMuted}>{formatNumber(feature.total_tokens)}</span>
                </div>
                <div className={cn("text-xs mt-1", colors.textMuted)}>
                  {feature.request_count} requests · {feature.unique_orgs} orgs
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No data state */}
      {byModel.length === 0 && byFeature.length === 0 && (
        <div className="text-center py-6">
          <Database className={cn("w-12 h-12 mx-auto mb-2", colors.textMuted)} />
          <p className={cn("text-sm", colors.textMuted)}>No token usage recorded yet</p>
          <p className={cn("text-xs", colors.textMuted)}>AI feature usage will appear here</p>
        </div>
      )}
    </Card>
  );
};

export default TokenUtilizationDashboard;
